import React, { useEffect, useRef, useState } from 'react';
import { useOtherServiceSales } from './OtherServiceSalesContext';
import {
  SUCCESS_THEN_REDIRECT_MS,
  waitUntilMinElapsed,
} from './utils/submitFeedback';
import { roundMoney2 } from './utils/money';
import { useAuth } from './AuthContext';
import { otherServiceOptionsForUser } from './constants/otherServicePresets';
import './OtherServicesForm.css';

const OtherServicesForm = ({ onNavigate }) => {
  const { user } = useAuth();
  const serviceOptions = otherServiceOptionsForUser(user);
  const { recordSale } = useOtherServiceSales();
  const [service, setService] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [phase, setPhase] = useState('idle');
  const [message, setMessage] = useState(null);
  const redirectRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, []);

  const busy = phase === 'loading' || phase === 'success';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase !== 'idle') return;
    if (redirectRef.current) {
      clearTimeout(redirectRef.current);
      redirectRef.current = null;
    }
    setMessage(null);
    const opt = serviceOptions.find((o) => o.value === service);
    const label = opt?.label || service;
    const amt = roundMoney2(parseFloat(String(amount).replace(',', '.')));
    if (!Number.isFinite(amt) || amt <= 0) {
      setMessage({ type: 'error', text: 'Enter a valid amount.' });
      return;
    }
    const desc =
      service === 'other'
        ? description.trim()
        : [label, description.trim()].filter(Boolean).join(' — ').slice(0, 500);
    if (service === 'other' && !desc) {
      setMessage({ type: 'error', text: 'Specify the service name.' });
      return;
    }

    setPhase('loading');
    const started = Date.now();
    try {
      await recordSale({
        serviceCode: service,
        description: desc,
        amountTzs: amt,
      });
      await waitUntilMinElapsed(started);
      setService('');
      setDescription('');
      setAmount('');
      setPhase('success');
      setMessage({ type: 'success', text: 'Recorded. Not added to wakala cashbook — counts in revenue / profit only.' });
      redirectRef.current = setTimeout(() => {
        redirectRef.current = null;
        setPhase('idle');
        setMessage(null);
        if (typeof onNavigate === 'function') onNavigate('overview');
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Could not save. Try again.' });
      setPhase('idle');
    }
  };

  return (
    <div className="other-services-form-root page-bleed">
      <div className="other-services-form-card">
        <h2 className="other-services-form-title">Other Services Sales</h2>
        <p className="other-services-form-hint">
          Separate mini-ledger (not the wakala cashbook). Amounts still feed owner dashboards and profit breakdowns.
        </p>
        <form className="other-services-form-fields" onSubmit={handleSubmit}>
          <div className="other-services-form-group">
            <label>Service Type:</label>
            <select value={service} onChange={(e) => setService(e.target.value)} required disabled={busy}>
              <option value="">--Select--</option>
              {serviceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {service === 'other' && (
            <div className="other-services-form-group">
              <label>Specify Service:</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                disabled={busy}
              />
            </div>
          )}
          {service !== 'other' && service && (
            <div className="other-services-form-group">
              <label>Description (optional):</label>
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy} />
            </div>
          )}
          <div className="other-services-form-group">
            <label>Amount (TZS):</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required disabled={busy} />
          </div>
          <button className="other-services-form-btn" type="submit" disabled={busy}>
            {phase === 'loading' ? (
              <>
                <span className="submit-spinner" aria-hidden />
                Saving…
              </>
            ) : phase === 'success' ? (
              'Redirecting…'
            ) : (
              'Save'
            )}
          </button>
        </form>
        {message?.type === 'success' && <div className="form-message-global success">{message.text}</div>}
        {message?.type === 'error' && <div className="form-message-global error">{message.text}</div>}
      </div>
    </div>
  );
};

export default OtherServicesForm;
